import React, { useState } from 'react';
import { Asset } from '../types';

interface AssetListProps {
  assets: Asset[];
  onRemoveAsset: (id: string) => void;
  onUpdateAsset: (updatedAsset: Asset) => void;
}

const AssetList: React.FC<AssetListProps> = ({ assets, onRemoveAsset, onUpdateAsset }) => {
  const [editingAssetId, setEditingAssetId] = useState<string | null>(null);
  const [editingValues, setEditingValues] = useState<{
    name: string;
    amount: string;
    interestRate: string;
    volatility: string;
    annualContribution: string;
  }>({
    name: '',
    amount: '',
    interestRate: '',
    volatility: '',
    annualContribution: ''
  });
  
  const totalValue = assets.reduce((sum, asset) => sum + asset.amount, 0);
  
  const startEditing = (asset: Asset) => {
    setEditingAssetId(asset.id);
    setEditingValues({
      name: asset.name,
      amount: asset.amount.toString(),
      interestRate: asset.interestRate.toString(),
      volatility: asset.volatility.toString(),
      annualContribution: asset.annualContribution ? asset.annualContribution.toString() : '0'
    });
  };
  
  const cancelEditing = () => {
    setEditingAssetId(null);
  };
  
  const saveEditing = (id: string) => {
    onUpdateAsset({
      id,
      name: editingValues.name,
      amount: parseFloat(editingValues.amount),
      interestRate: parseFloat(editingValues.interestRate),
      volatility: parseFloat(editingValues.volatility),
      annualContribution: parseFloat(editingValues.annualContribution) || 0
    });
    setEditingAssetId(null);
  };
  
  const handleInputChange = (field: keyof typeof editingValues, value: string) => {
    setEditingValues({
      ...editingValues,
      [field]: value
    });
  };
  
  if (assets.length === 0) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-md mb-6">
        <h2 className="text-xl font-bold mb-2">Your Assets</h2>
        <p className="text-gray-500">No assets added yet. Add your first asset using the form above.</p>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow-md mb-6">
      <h2 className="text-xl font-bold mb-4">Your Assets</h2>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Interest Rate</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Volatility</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Annual Contribution</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {assets.map((asset) => (
              <tr key={asset.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  {editingAssetId === asset.id ? (
                    <input
                      type="text"
                      value={editingValues.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className="w-full px-2 py-1 border border-gray-300 rounded-md"
                    />
                  ) : (
                    asset.name
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {editingAssetId === asset.id ? (
                    <input
                      type="number"
                      value={editingValues.amount}
                      onChange={(e) => handleInputChange('amount', e.target.value)}
                      className="w-full px-2 py-1 border border-gray-300 rounded-md"
                      min="0"
                      step="1000"
                    />
                  ) : (
                    `$${asset.amount.toLocaleString()}`
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {editingAssetId === asset.id ? (
                    <input
                      type="number"
                      value={editingValues.interestRate}
                      onChange={(e) => handleInputChange('interestRate', e.target.value)}
                      className="w-full px-2 py-1 border border-gray-300 rounded-md"
                      min="0"
                      max="100"
                      step="0.1"
                    />
                  ) : (
                    `${asset.interestRate}%`
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {editingAssetId === asset.id ? (
                    <input
                      type="number"
                      value={editingValues.volatility}
                      onChange={(e) => handleInputChange('volatility', e.target.value)}
                      className="w-full px-2 py-1 border border-gray-300 rounded-md"
                      min="0"
                      max="100"
                      step="0.1"
                    />
                  ) : (
                    `${asset.volatility}%`
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {editingAssetId === asset.id ? (
                    <input
                      type="number"
                      value={editingValues.annualContribution}
                      onChange={(e) => handleInputChange('annualContribution', e.target.value)}
                      className="w-full px-2 py-1 border border-gray-300 rounded-md"
                      min="0"
                      step="100"
                    />
                  ) : (
                    `$${asset.annualContribution ? asset.annualContribution.toLocaleString() : '0'}`
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {editingAssetId === asset.id ? (
                    <div className="flex space-x-2">
                      <button
                        onClick={() => saveEditing(asset.id)}
                        className="text-green-600 hover:text-green-800"
                      >
                        Save
                      </button>
                      <button
                        onClick={cancelEditing}
                        className="text-gray-600 hover:text-gray-800"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div className="flex space-x-2">
                      <button
                        onClick={() => startEditing(asset)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => onRemoveAsset(asset.id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            
            <tr className="bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap font-semibold">Total</td>
              <td className="px-6 py-4 whitespace-nowrap font-semibold">${totalValue.toLocaleString()}</td>
              <td className="px-6 py-4 whitespace-nowrap"></td>
              <td className="px-6 py-4 whitespace-nowrap"></td>
              <td className="px-6 py-4 whitespace-nowrap font-semibold">${assets.reduce((sum, asset) => sum + (asset.annualContribution || 0), 0).toLocaleString()}</td>
              <td className="px-6 py-4 whitespace-nowrap"></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AssetList;